import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import FormInput from 'components/common/form-input';
import Tooltip from 'components/layout/tooltip';
import { CeilingBelowAtticType, SizingItemsType } from 'data/types';
import { useState } from 'react';
import { isNotEmpty } from 'utils/helpers';
import {
  getValidSizingAttributeOptions,
  isValidSizingItem
} from 'utils/sizing';

type CeilingBelowAtticProps = {
  ceiling: CeilingBelowAtticType;
  updateFn: (
    ceilingId: string,
    updates: Partial<CeilingBelowAtticType>
  ) => void;
  duplicateFn: (ceilingId: string) => void;
  deleteFn: (ceilingId: string) => void;
  updateDefaultSizingFn: (updates: Partial<SizingItemsType>) => void;
};

export default function CeilingBelowAtticComponent({
  ceiling,
  updateFn,
  duplicateFn,
  deleteFn,
  updateDefaultSizingFn
}: CeilingBelowAtticProps) {
  const defaultValues = useAppSelector(
    (state) => state.data.defaultSizingItems.ceilingBelowAttic
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [animate] = useAutoAnimate({ duration: 100 });
  const [deleteQueued, setDeleteQueued] = useState(false);
  const sizingItemType = 'ceilingsBelowAttic';
  const isModified = Object.keys(defaultValues)
    .filter((key) => isNotEmpty(defaultValues[key]))
    .some((key) => ceiling[key] !== defaultValues[key]);

  const handleUpdateWithValidation = (
    changes: Partial<CeilingBelowAtticType>
  ) => {
    const newCeiling: CeilingBelowAtticType = {
      ...ceiling,
      ...changes
    };
    const isValid = isValidSizingItem(sizingItemType, newCeiling);

    updateFn(ceiling.uniqueId, {
      ...newCeiling,
      valid: isValid
    });
  };

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions((prev) => !prev);
  };

  const handleDuplicateClick = () => {
    duplicateFn(ceiling.uniqueId);
  };

  const handleDeleteClick = () => {
    if (!deleteQueued) {
      setDeleteQueued(true);
      return;
    }

    deleteFn(ceiling.uniqueId);
    setDeleteQueued(false);
  };

  const setAsDefault = () => {
    updateDefaultSizingFn({
      ceilingBelowAttic: {
        ...ceiling,
        uniqueId: undefined,
        valid: undefined,
        area: undefined
      }
    });
  };

  const classes = clsx(
    'flex flex-col gap-3 rounded-xl border-2 bg-gray-100',
    ceiling.valid ? 'border-gray-200' : 'border-red-500'
  );

  return (
    <div className={classes}>
      <div
        key={ceiling.uniqueId}
        className='flex w-full flex-col divide-y divide-gray-300 rounded-lg bg-gray-200 px-5'
        ref={animate}
      >
        <div className='flex min-h-[54px] items-center gap-5'>
          <div className='font-semibold'>Ceiling Below Attic</div>
          <div className='w-[150px]'>
            <FormInput
              value={ceiling.area}
              prefix='Area: '
              placeholder='Area (sq ft)'
              type='number'
              suffix=' sq ft'
              min={1}
              onChange={(value) => handleUpdateWithValidation({ area: value })}
              required
            />
          </div>
          {!ceiling.valid && (
            <span className='text-red-500'>Invalid Attributes!</span>
          )}
          <div className='ml-auto flex items-center gap-2'>
            <button
              className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
              type='button'
              onClick={toggleAdvancedOptions}
            >
              <span>Advanced</span>
              {showAdvancedOptions ? (
                <ChevronUpIcon className='h-5 w-5' />
              ) : (
                <ChevronDownIcon className='h-5 w-5' />
              )}
              <Tooltip
                text={
                  showAdvancedOptions
                    ? 'Hide Advanced Options'
                    : 'Show Advanced Options'
                }
              />
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDuplicateClick}
            >
              <DocumentDuplicateIcon className='h-5 w-5' />
              <Tooltip text='Duplicate Ceiling' />
            </button>
            <button
              className='relative h-full cursor-pointer text-gray-500 hover:text-gray-700'
              type='button'
              onClick={handleDeleteClick}
              onMouseLeave={() => setDeleteQueued(false)}
            >
              <TrashIcon className='h-5 w-5' />
              <Tooltip
                text={
                  deleteQueued ? 'Click Again to Confirm' : 'Delete Ceiling'
                }
              />
            </button>
          </div>
        </div>
        {showAdvancedOptions && (
          <div className='flex flex-wrap items-center gap-x-5 gap-y-3 py-3'>
            <FormInput
              value={ceiling.insulationValue}
              options={getValidSizingAttributeOptions(
                'ceilingsBelowAttic',
                ceiling,
                'insulationValue'
              )}
              onChange={(value) =>
                handleUpdateWithValidation({
                  insulationValue: value
                })
              }
              label='Insulation Value'
              type='select'
              required
              preventDeselection
            />
            {isModified && (
              <div className='flex w-full justify-end'>
                <button
                  className='relative flex h-full cursor-pointer items-center text-gray-500 hover:text-gray-700'
                  type='button'
                  onClick={setAsDefault}
                >
                  <span>Set as Default</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
